import { NavLink, useNavigate } from "react-router-dom";
import {
  Home, Inbox, Users, Building2, Briefcase, FileSignature, BarChart3, Settings,
  CheckSquare, Megaphone, ClipboardList, FileText, Workflow, ListChecks,
  Mail, ShieldCheck, FolderKanban,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useChordShortcuts } from "@/hooks/useKeyboardShortcuts";
import { CONTRACT_BUILDER_ENABLED } from "@/lib/flags";

// Sidebar IA per MASTER_PROMPT §2.1.
// Five groups: Workspace · CRM · Loops · Marketing · Insights · Client portal.
// Active row: 2 px left rail in `--accent`, background `--bg-active`, text
// `--text`. Hover: `--bg-hover`. Group headers: text-xs uppercase tracking-wide.

interface NavLeaf {
  to: string;
  label: string;
  icon: typeof Inbox;
  chord?: string;
  /** Optional badge text — unread count, due-today count, etc. */
  badge?: string | number;
}

interface NavGroup {
  title?: string;
  items: NavLeaf[];
}

const GROUPS: NavGroup[] = [
  {
    title: "Workspace",
    items: [
      { to: "/", label: "Home", icon: Home, chord: "h" },
      { to: "/inbox", label: "Inbox", icon: Inbox, chord: "i" },
      { to: "/tasks", label: "Tasks", icon: CheckSquare, chord: "k" },
      { to: "/admin/audit", label: "Approvals", icon: ShieldCheck },
    ],
  },
  {
    title: "CRM",
    items: [
      { to: "/contacts", label: "Contacts", icon: Users, chord: "c" },
      { to: "/companies", label: "Companies", icon: Building2, chord: "o" },
      { to: "/deals", label: "Deals", icon: Briefcase, chord: "d" },
      { to: "/contracts", label: "Contracts", icon: FileSignature, chord: "t" },
    ],
  },
  {
    title: "Loops",
    items: [
      { to: "/workflows", label: "Workflows", icon: Workflow },
      { to: "/lists", label: "Lists", icon: ListChecks },
    ],
  },
  {
    title: "Marketing",
    items: [
      { to: "/sequences", label: "Sequences", icon: Mail },
      { to: "/campaigns", label: "Campaigns", icon: Megaphone },
      { to: "/templates", label: "Templates", icon: FileText },
      { to: "/forms", label: "Forms", icon: ClipboardList },
    ],
  },
  {
    title: "Insights",
    items: [
      { to: "/reports", label: "Reports", icon: BarChart3, chord: "r" },
    ],
  },
  {
    title: "Client portal",
    items: [
      { to: "/projects", label: "Projects", icon: FolderKanban },
    ],
  },
];

// The in-repo contract builder is archived (client contracts live in Monjur —
// ourterms.live). Hide its "Contracts" nav entry unless CONTRACT_BUILDER_ENABLED
// is set. See docs/adr/ADR-archive-contract-builder.md.
const NAV_GROUPS = CONTRACT_BUILDER_ENABLED
  ? GROUPS
  : GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => i.to !== "/contracts") }));

export default function Sidebar() {
  const navigate = useNavigate();
  // `g <letter>` chord shortcuts (g h, g i, g c, …) for keyboard navigation
  // per MASTER_PROMPT §1 (Non-negotiable: keyboard-first).
  useChordShortcuts(
    NAV_GROUPS.flatMap((g) =>
      g.items
        .filter((i) => i.chord)
        .map((i) => ({
          keys: ["g", i.chord!] as [string, string],
          handler: () => navigate(i.to),
          description: `Go to ${i.label}`,
        })),
    ),
  );

  return (
    <aside className="flex h-full w-[220px] shrink-0 flex-col bg-bg-sidebar text-text-2 border-r border-border-soft">
      <div className="flex h-[48px] items-center gap-2 border-b border-border-soft px-3">
        <div className="grid h-7 w-7 place-items-center rounded-md bg-accent text-accent-fg">
          <span className="font-display text-[11px] font-bold">LG</span>
        </div>
        <div className="flex flex-col leading-none">
          <span className="font-display text-base font-semibold tracking-tight text-text">
            LuzGrid
          </span>
          <span className="mt-0.5 text-[10px] uppercase tracking-[1.2px] text-text-3">
            CRM
          </span>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2 scrollbar-thin">
        {NAV_GROUPS.map((group, gi) => (
          <div key={group.title ?? gi} className={gi === 0 ? "" : "mt-3"}>
            {group.title && (
              <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[1.2px] text-text-3">
                {group.title}
              </div>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <NavItem key={item.to} {...item} end={item.to === "/"} />
              ))}
            </ul>
          </div>
        ))}

        <div className="mt-3">
          <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[1.2px] text-text-3">
            Admin
          </div>
          <ul className="space-y-0.5">
            <NavItem to="/admin/users" label="Settings" icon={Settings} />
          </ul>
        </div>
      </nav>
    </aside>
  );
}

function NavItem({ to, label, icon: Icon, chord, badge, end }: NavLeaf & { end?: boolean }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          cn(
            "group relative flex h-8 items-center gap-2.5 rounded-md px-2.5 text-base font-medium transition-colors duration-[120ms] ease-lz",
            isActive
              ? "bg-bg-active text-text"
              : "text-text-2 hover:bg-bg-hover hover:text-text",
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                aria-hidden
                className="absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-r bg-accent"
              />
            )}
            <Icon className={cn("h-4 w-4 shrink-0", isActive ? "opacity-100" : "opacity-80")} />
            <span className="flex-1 truncate">{label}</span>
            {badge != null && (
              <span className="rounded-full bg-bg-card2 px-1.5 text-[10px] font-semibold leading-[14px] text-text-2">
                {badge}
              </span>
            )}
            {chord && (
              <kbd className="hidden font-mono text-[9px] text-text-3 group-hover:inline">
                g {chord}
              </kbd>
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}
